// Presentation helpers of Routing Intelligence v2 (decision D24): tier pill (icon + text), task class, classifier and
// cache labels, the analysis window control, and costs that stay "unknown" instead of zero.
import type { Translate } from "../../i18n/index.tsx";
import { useI18n } from "../../i18n/index.tsx";
import type { IconKind, Tone } from "../../lib/status.ts";
import { formatCents } from "../../lib/format.ts";
import { Pill } from "../../components/Status.tsx";
import { labelOr } from "../wave5/common.tsx";
import type { CacheState, ModelTier, RoutingDecision } from "../../api/types-routing.ts";

export const WINDOWS = [7, 30, 90] as const;

const TIER_VIEW: Record<ModelTier, { tone: Tone; icon: IconKind }> = {
  deterministic: { tone: "neutral", icon: "circle" },
  economy: { tone: "info", icon: "dot" },
  premium: { tone: "warning", icon: "activity" },
};

export function TierPill({ tier }: { tier: ModelTier }) {
  const { t } = useI18n();
  const view = TIER_VIEW[tier] ?? TIER_VIEW.deterministic;
  return (
    <Pill tone={view.tone} icon={view.icon}>
      {labelOr(t, `ri.tier.${tier}`, tier)}
    </Pill>
  );
}

/** "backend/implementation/medium" → "Backend / Implementation / Medium"; any other shape is shown as sent. */
export function classLabel(t: Translate, taskClass: string): string {
  const parts = taskClass.split("/");
  if (parts.length !== 3) return taskClass;
  const [domain, kind, complexity] = parts as [string, string, string];
  return [labelOr(t, `ri.domain.${domain}`, domain), labelOr(t, `ri.kind.${kind}`, kind), labelOr(t, `ri.complexity.${complexity}`, complexity)].join(" / ");
}

export const cacheLabel = (t: Translate, state: CacheState) => labelOr(t, `ri.cache.${state}`, state);
export const sourceLabel = (t: Translate, source: string) => labelOr(t, `ri.source.${source}`, source);

/** "typesafe/jev-1.13" → "Jev 1.13"; degraded decisions say so even when Jev was attempted. */
export function classifierLabel(t: Translate, d: Pick<RoutingDecision, "source" | "jev_model">): string {
  if (d.source === "degraded" || !d.jev_model) return sourceLabel(t, d.source === "degraded" ? "degraded" : "rules");
  const version = /jev-(\d[\w.]*)$/i.exec(d.jev_model)?.[1];
  const name = version ? `Jev ${version}` : d.jev_model;
  return d.source === "jev" ? name : `${name} · ${sourceLabel(t, d.source)}`;
}

/** Cost in cents, "unknown" when the server does not know it, flagged when some attempts had no price. */
export function PartialCost({ cents, unknownAttempts }: { cents: number | null; unknownAttempts: number }) {
  const { t, locale } = useI18n();
  return (
    <span className="cost-text" data-testid="ri-cost">
      {cents === null ? <span className="subtle">{t("ri.unknown")}</span> : formatCents(cents, locale)}
      {unknownAttempts > 0 ? (
        <span className="badge badge--warning cost-unknown" data-testid="ri-partial-cost">
          {t("ri.perf.partialCost", { n: unknownAttempts })}
        </span>
      ) : null}
    </span>
  );
}

export function formatUsd(value: number | null, locale: string, t: Translate): string {
  return value === null ? t("ri.unknown") : new Intl.NumberFormat(locale, { style: "currency", currency: "USD", maximumFractionDigits: 4 }).format(value);
}

export function WindowControl({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  const { t } = useI18n();
  return (
    <div className="segmented" role="group" aria-label={t("agents.scores.window")}>
      {WINDOWS.map((w) => (
        <button key={w} type="button" aria-pressed={days === w} onClick={() => onChange(w)}>
          {t("agents.scores.days", { n: w })}
        </button>
      ))}
    </div>
  );
}
